import React from 'react';
import { 
  Group, 
  Lock, 
  Unlock, 
  AlignLeft, 
  AlignCenter, 
  AlignRight,
  Copy,
  Trash2
} from 'lucide-react';

interface FloatingContextToolbarProps {
  selectedIds: string[];
  selectionBounds: { x: number; y: number; width: number; height: number } | null;
  canGroup: boolean;
  allLocked: boolean;
  onGroup: () => void;
  onToggleLock: () => void;
  onAlign?: (type: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  canvasScale: number;
  canvasRef?: React.RefObject<HTMLDivElement>;
}

export const FloatingContextToolbar: React.FC<FloatingContextToolbarProps> = ({
  selectedIds,
  selectionBounds,
  canGroup,
  allLocked,
  onGroup,
  onToggleLock,
  onAlign,
  onDuplicate,
  onDelete,
  canvasScale,
  canvasRef
}) => {
  if (!selectionBounds || selectedIds.length < 2) {
    return null;
  }

  // Calculate toolbar position in screen coordinates
  const TOOLBAR_OFFSET = 12;
  const TOOLBAR_HEIGHT = 48;
  
  // Get canvas position in viewport if canvasRef is provided
  let canvasOffsetX = 0;
  let canvasOffsetY = 0;
  
  if (canvasRef?.current) {
    const canvasRect = canvasRef.current.getBoundingClientRect();
    canvasOffsetX = canvasRect.left;
    canvasOffsetY = canvasRect.top;
  }

  // Convert canvas coordinates to screen coordinates
  const screenX = canvasOffsetX + (selectionBounds.x * canvasScale);
  const screenY = canvasOffsetY + (selectionBounds.y * canvasScale);
  const screenWidth = selectionBounds.width * canvasScale;

  const top = screenY - TOOLBAR_OFFSET - TOOLBAR_HEIGHT;
  const left = screenX + screenWidth / 2;

  return (
    <div
      className="fixed z-[100] flex items-center gap-1 bg-white rounded-full shadow-2xl border border-gray-200 px-2 py-2"
      style={{
        top: `${top}px`,
        left: `${left}px`,
        transform: 'translateX(-50%)',
        pointerEvents: 'auto'
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Group Button */}
      <button
        type="button"
        onClick={onGroup}
        disabled={!canGroup}
        className={`p-2 rounded-full text-sm font-medium transition-colors ${
          canGroup
            ? 'text-gray-700 hover:bg-gray-100'
            : 'text-gray-400 cursor-not-allowed'
        }`}
        title="Group elements"
      >
        <Group className="w-4 h-4" />
      </button>

      {/* Lock/Unlock Button */}
      <button
        type="button"
        onClick={onToggleLock}
        className="p-2 rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
        title={allLocked ? 'Unlock' : 'Lock'}
      >
        {allLocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
      </button>

      <div className="w-px h-6 bg-gray-200" />

      {/* Duplicate Button */}
      <button
        type="button"
        onClick={onDuplicate}
        className="p-2 rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
        title="Duplicate selection"
      >
        <Copy className="w-4 h-4" />
      </button>

      {/* Delete Button */}
      <button
        type="button"
        onClick={onDelete}
        className="p-2 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
        title="Delete selection"
      >
        <Trash2 className="w-4 h-4" />
      </button>

      <div className="w-px h-6 bg-gray-200" />

      {/* Alignment Tools */}
      <div className="flex items-center gap-0.5 px-1">
        <button
          type="button"
          onClick={() => onAlign?.('left')}
          className="p-2 rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
          title="Align Left"
        >
          <AlignLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onAlign?.('center')}
          className="p-2 rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
          title="Align Center"
        >
          <AlignCenter className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onAlign?.('right')}
          className="p-2 rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
          title="Align Right"
        >
          <AlignRight className="w-4 h-4" />
        </button>
        
        <div className="w-px h-4 bg-gray-100 mx-1" />
        
        <button
          type="button"
          onClick={() => onAlign?.('top')}
          className="p-2 rounded-full text-gray-700 hover:bg-gray-100 transition-colors rotate-90"
          title="Align Top"
        >
          <AlignLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onAlign?.('middle')}
          className="p-2 rounded-full text-gray-700 hover:bg-gray-100 transition-colors rotate-90"
          title="Align Middle"
        >
          <AlignCenter className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onAlign?.('bottom')}
          className="p-2 rounded-full text-gray-700 hover:bg-gray-100 transition-colors rotate-90"
          title="Align Bottom"
        >
          <AlignRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
